<script>
// Components
import Layout from "@/router/layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config.json";
import UserProfile from "@/router/views/users/user-profile"

// Helpers
import {authComputed} from "@/state/helpers";
import DateUtils from '@/helpers/date';
import UserUtils from '@/helpers/users';
import Storage from "@/helpers/storage"

// Services
import EmailsService from "@/services/EmailsService";
import MarketingDataService from "@/services/MarketingDataService";
import UsersDataService from "@/services/UsersDataService"

// External Components
import DatePicker  from "vue2-datepicker";
import 'vue2-datepicker/index.css';
import {VueTabs, VTab} from 'vue-nav-tabs'
import 'vue-nav-tabs/themes/vue-tabs.css';
import CoolLightBox from 'vue-cool-lightbox';
import 'vue-cool-lightbox/dist/vue-cool-lightbox.min.css';

// Lib
import isEmpty from "is-empty";


export default {
  name: "emails",
  page: {
    localePicker: () => import(`vue2-datepicker/locale/${this.$i18n.locale}`),
    title: "Emails Manager",
    meta: [
      {
        name: "description",
        content: appConfig.description
      },
    ],
  },

  components: {
    Layout,
    PageHeader,
    DatePicker,
    VueTabs,
    VTab,
    UserProfile,
    CoolLightBox,
  },

  computed: {
    ...authComputed
  },

  methods: {
    ...UserUtils,
    ...DateUtils,

    handlePageChange(value) {
      this.page = value;
      if (this.emailsSearch === 'filters')
        this.loadEmails();
      else if (this.emailsSearch === 'search')
        this.processSearch(false);
    },

    handleDisplayChange() {
      this.loadEmails();
    },

    handleDateChange() {
      let now = new this.$moment();
      switch(this.filters.dates.selected) {
        case "always":
          this.filters.fromDate = new this.$moment('2021-01-01').format('YYYY-MM-DD');
          this.filters.toDate = now.format('YYYY-MM-DD');
          break;
        case "today":
          this.filters.fromDate = now.format('YYYY-MM-DD');
          this.filters.toDate = now.format('YYYY-MM-DD');
          break;
        case "yesterday":
          this.filters.fromDate = now.subtract(1, 'day').format('YYYY-MM-DD');
          this.filters.toDate = this.filters.fromDate;
          break;
        case "week":
          this.filters.toDate = now.format('YYYY-MM-DD');
          this.filters.fromDate = now.subtract(1, 'week').startOf('day').format('YYYY-MM-DD');
          break;
        case "curMonth":
          this.filters.toDate = now.format('YYYY-MM-DD');
          this.filters.fromDate = now.startOf('month').format('YYYY-MM-DD');
          break;
        case "last30Day":
          this.filters.toDate = now.format('YYYY-MM-DD');
          this.filters.fromDate = now.subtract(30, 'day').startOf('day').format('YYYY-MM-DD');
          break;
        case "lastMonth":
          this.filters.fromDate = now.subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
          this.filters.toDate = now.endOf('month').format('YYYY-MM-DD');
          break;
        case "last3Month":
          this.filters.toDate = now.format('YYYY-MM-DD');
          this.filters.fromDate = now.subtract(3, 'month').startOf('day').format('YYYY-MM-DD');
          break;
        case "curYear":
          this.filters.toDate = now.format('YYYY-MM-DD');
          this.filters.fromDate = now.startOf('year').format('YYYY-MM-DD');
          break;
        case "custom":
          this.filters.fromDate = null;
          this.filters.toDate = null;
          break;
      }
    },

    getRequestParams() {
      const params = {};

      params["page"] = this.page - 1;
      params["size"] = this.pageSizes.selected;

      if (this.filters.dates.selected === 'custom' && this.filters.dates.dateRange !== null) {
        this.filters.fromDate = this.$moment(this.filters.dates.dateRange[0]).format("YYYY-MM-DD 00:00:00");
        this.filters.toDate = this.$moment(this.filters.dates.dateRange[1]).format("YYYY-MM-DD 23:59:59");
      }
      else if (this.filters.fromDate === null)
        this.handleDateChange();

      if (this.filters.toDate !== null)
        params['toDate'] = this.filters.toDate;

      if (this.filters.fromDate !== null)
        params['fromDate'] = this.filters.fromDate;

      params['gender'] = this.filters.genders.selected;
      params['domains'] = this.filters.domains.selected;
      params['types'] = this.filters.types.selected;
      params['status'] = this.filters.status.selected;
      params['organic'] = this.filters.organic.selected;
      params['ads'] = this.filters.ads.selected;
      params['countries'] = this.filters.countries.selected;

      if (this.$store.state.layout.timezone === 'utc') {
        params['tz'] = this.$moment.tz.guess();
      }

      return params;
    },

    loadStats() {
      const params = this.getRequestParams();
      EmailsService
          .getStats(params)
          .then(response => {

            const {total, types, domains, historyGraph} = response.data;
            this.stats.delivered = total.delivered;
            this.stats.opened = total.opened;
            this.stats.clicked = total.clicked;
            this.stats.errorPermanent = total.errorPermanent;
            this.stats.errorEsp = total.errorEsp;
            this.stats.errorTmp = total.errorTmp;
            this.stats.error = total.error;
            this.stats.unsubscribe = total.unsubscribe;
            this.stats.certified = total.certified;
            this.stats.valided = total.valided;
            this.stats.complained = total.complained;
            this.stats.validedPerc = total.validedPerc;
            this.stats.certifiedPerc = total.certifiedPerc;

            this.typesGraph.series = types.series;
            this.typesGraph.chartOptions = {...this.typesGraph.chartOptions, ...{
                labels: types.labels
              }};

            this.domainsGraph.series = domains.series;
            this.domainsGraph.chartOptions = {...this.domainsGraph.chartOptions, ...{
                labels: domains.labels
              }};

            this.$refs.emailsHistoryChart.updateSeries([{
              name: "Delivered",
              type: 'bar',
              data: historyGraph.delivered
            },{
              name: "Opened",
              type: 'bar',
              data: historyGraph.opened
            }, {
              name: "Clicked",
              type: 'bar',
              data: historyGraph.clicked
            }, {
              name: "Error",
              type: 'bar',
              data: historyGraph.error
            }]);
          })
          .catch(error => {
            window.console.error(error);
          });
    },

    getFilters() {
      MarketingDataService.getAdsNonOrganicFilters()
          .then(response => {
            response.data.filters.ads.forEach(ad => {
              this.filters.ads.options.push(ad);
            });
          })
          .catch(error => {
            window.console.log(error);
            this.$toast.error(this.$t("marketing.getAdsNonOrganicFilters"));
          });

      EmailsService.getFilters()
          .then(response => {
            response.data.filters.types.forEach(type => {
              this.filters.types.options.push(type);
            });
          })
          .catch(error => {
            window.console.log(error);
            this.$toast.error(this.$t("emails.getFiltersError"));
          });
    },

    loadEmails() {
      this.isLoading = true;
      const params = this.getRequestParams();
      this.emailsSearch = 'filters';
      EmailsService
          .getEmails(params)
          .then(response => {
            const { items, totalItems } = response.data;
            this.emails = items;
            this.count = totalItems;
            this.getMediaThumb();
          })
          .catch(error => {
            window.console.error(error);
            this.$toast.error(this.$t("emails.errorEmailsNoResult"));
          })
          .finally(() => {
            this.isLoading = false;
          });
    },

    resetData() {
      this.emails = [];
      this.stats = {
        delivered: '-',
        opened: '-',
        clicked: '-',
        errorEsp: '-',
        errorPermanent: '-',
        errorTmp: '-',
        error: '-',
        unsubscribe: '-',
        certifiedPerc: '-',
        certified: '-',
        validedPerc: '-',
        valided: '-',
        complained: '-',
      };
    },

    processFilters() {
      this.resetData();
      this.loadEmails();
      this.loadStats();
    },

    processSearch(resetPage) {
      if (isEmpty(this.filters.searchType)) {
        this.$toast.error(this.$t("emails.error_searchType"));
        return;
      }

      if (isEmpty(this.filters.searchData)) {
        this.$toast.error(this.$t("emails.error_searchData"));
        return;
      }

      this.emailsSearch = 'search';
      if (resetPage) {
        this.page = 1;
      }

      this.isLoading = true;
      const params = {
        type: this.filters.searchType.selected,
        data: this.filters.searchData,
        page: this.page - 1,
        size: this.pageSizes.selected,
      };

      EmailsService
          .search(params)
          .then(response => {
            const { items, totalItems } = response.data;
            this.emails = items;
            this.count = totalItems;
            this.isLoading = false;
            this.getMediaThumb();
          })
          .catch(error => {
            window.console.error(error);
            this.$toast.error(this.$t("emails.errorEmailsNoResult"));
            this.isLoading = false;
          });
    },

    getMediaThumb() {
      if (this.emails.length > 0)
        this.emails.forEach(email => {
          if (email.photo && email.photo.length > 0)
            Storage.getUserMedia(email.userId, email.photo, 'email-user-'+email.emailId);

        });
    },

    _isEmpty(val) {
      return isEmpty(val);
    },

    searchUser(userId) {
      this.filters.searchType.selected = 'userId';
      this.filters.searchData = userId;
      this.processSearch(true);
    },

    showContextMenu(event, user) {
      this.$refs.emailsContextMenu.showMenu(event, user)
    },

    processContextMenu(event) {
      const { item, option } = event;
      switch (option.value) {
        case "show":
          this.showUser(item.userId);
          break;

        case "search":
          this.searchUser(item.userId);
          break;
      }
    },

    /* User profile */
    openUserPane() {
      if (!this.isUserPaneDisplayed)
        this.isUserPaneDisplayed = true;
    },

    closeUserPane() {
      this.userTabsActive = '';
      this.isUserPaneDisplayed = false;
    },

    async showUser(userId) {
      const isUserAlreadyDisplayed = this.userTabs.findIndex(x => x.userId === userId);
      let forceShow = false;
      if (isUserAlreadyDisplayed === -1) {
        try {
          const result = await UsersDataService.get(userId);
          if (typeof result.data === 'undefined')
            throw new Error('unable to find user '+userId);

          this.userTabs.push(UserUtils.normalize(result.data));
          forceShow = true;
        }
        catch (error) {
          window.console.error(error);
          this.$toast.error(this.$t("global_error"));
        }
      }

      this.openUserPane();

      if (forceShow || (!forceShow && this.userTabsActive !== userId)) {
        this.userTabsActive = '';
        setTimeout(() => { this.userTabsActive = userId; }, 100);
      }
    },

    showUserMedia(media, index) {
      this.userMedia.media = media;
      this.userMedia.index = index;
    },

    removeUserTab(userId) {
      const index = this.userTabs.findIndex(x => x.userId === userId);
      if (index > -1)
        this.userTabs.splice(index, 1);

      if (this.userTabs.length === 0)
        this.closeUserPane();
    }
  },

  mounted() {
    this.getFilters();
    this.processFilters();
  },

  data() {
    return {
      isLoading: false,
      title: this.$t("menuitems.system.emails"),
      emailsSearch: 'filters',
      emails: [],
      page: 1,
      count: 0,
      pageSizes: {
        options: ['20', '50', '100'],
        selected: '20'
      },
      optionsContext: [
        {
          name: this.$t("show_user"),
          value: 'show'
        },
        {
          name: this.$t("search_user"),
          value: 'search'
        },
      ],
      stats: {
        delivered: '-',
        opened: '-',
        clicked: '-',
        errorEsp: '-',
        errorPermanent: '-',
        errorTmp: '-',
        error: '-',
        unsubscribe: '-',
        certifiedPerc: '-',
        certified: '-',
        validedPerc: '-',
        valided: '-',
        complained: '-',
      },
      typesGraph: {
        series:[],
        chartOptions: {
          chart: {
            id: 'emails-types',
            type: 'donut',
          },
          dataLabels: {
            enabled: true
          },
          labels:[],
          noData: {
            text: 'Loading...'
          },
          legend: {
            position: 'bottom'
          },
        }
      },
      domainsGraph: {
        series:[],
        chartOptions: {
          chart: {
            id: 'emails-domains',
            type: 'donut',
          },
          dataLabels: {
            enabled: true
          },
          labels:[],
          noData: {
            text: 'Loading...'
          },
          legend: {
            position: 'bottom'
          },
        }
      },
      historyGraph: {
        series: [],
        chartOptions: {
          chart: {
            stacked: true,
            type: 'bar',
            toolbar: {
              show: true,
            },
            zoom: {
              enabled: true,
            },
          },
          plotOptions: {
            bar: {
              horizontal: false,
              endingShape: "rounded",
              borderRadius: 10,
              dataLabels: {
                total: {
                  enabled: true,
                  style: {
                    fontSize: '13px',
                  }
                }
              }
            },
          },
          dataLabels: {
            enabled: false,
          },
          tooltip: {
            shared: true,
            intersect: false
          },
          xaxis: {
            type: 'datetime',
            labels: {
              show: true,
              style: {
                fontSize: 11
              },
              hideOverlappingLabels: true
            }
          },
          colors: ["#222222", "#34c38f", "#556ee6", "#f46a6a"],
          legend: {
            position: "bottom",
          },
          fill: {
            opacity: 1,
          },
        },
      },
      filters: {
        searchType: {
          selected: 'userId',
          options: [
            {value:'userId', text:this.$t("userId")},
            {value:'messageId', text:this.$t("emails.messageId")},
            {value:'email', text:this.$t("email")}
          ]
        },
        searchData: "",
        fromDate: null,
        toDate: null,
        dates: {
          dateRange: null,
          selected: 'week',
          options: [
            {value:'today', text:this.$t("dates.today")},
            {value:'yesterday', text:this.$t("dates.yesterday")},
            {value:'week', text:this.$t("dates.week")},
            {value:'curMonth', text:this.$t("dates.curMonth")},
            {value:'last30Day', text:this.$t("dates.last30Day")},
            {value:'lastMonth', text:this.$t("dates.lastMonth")},
            {value:'last3Month', text:this.$t("dates.last3Month")},
            {value:'curYear', text:this.$t("dates.curYear")},
            {value:'always', text:this.$t("dates.always")},
            {value:'custom', text:this.$t("dates.custom")}
          ]
        },
        organic: {
          selected: 'all',
          options: [
            {value: 'all', text: 'All Source'},
            {value: 'organic', text: 'Only Organic'},
            {value: 'marketing', text: 'Only Marketing'}
          ]
        },
        countries: {
          selected: 'all',
          options: [
            {value: 'all', text: 'All Countries'},
            {value: '', text: '----------------'},
            {value: 'FR', text: 'FR - France'},
            {value: 'BE', text: 'BE - Belgium'},
            {value: 'CH', text: 'CH - Switzerland'},
            {value: 'CA', text: 'CA - Canada'},
            {value: '', text: '----------------'},
            {value: 'others', text: 'Others'},
          ]
        },
        ads: {
          selected: 'all',
          options: [{
            value: 'all', text: 'All Ads'
          }]
        },
        genders: {
          selected: 'all',
          options: [
            {value: 'all', text: this.$t("users.all_genders")},
            {value: 1, text: this.$t("users.search.genderMale")},
            {value: 2, text: this.$t("users.search.genderFemale")}
          ]
        },
        domains: {
          selected: 'all',
          options: [
              {value: 'all', text: 'All Domains'},
              {value: 'spreedl.com', text: 'mg.spreedl.COM'},
              {value: 'spreedl.app', text: 'mg.spreedl.APP'},
              {value: 'spreedl.info', text: 'mg.spreedl.INFO'},
          ]
        },
        types: {
          selected: 'all',
          options: [{
            value: 'all', text: 'All Types'
          }]
        },
        status: {
          selected: 'all',
          options: [
            {value: 'all', text: this.$t("emails.status_all")},
            {value: 'D', text: this.$t("emails.status_D")},
            {value: 'O', text: this.$t("emails.status_O")},
            {value: 'A', text: this.$t("emails.status_A")},
            {value: 'E', text: this.$t("emails.status_E")},
            {value: 'F', text: this.$t("emails.status_F")},
            {value: 'T', text: this.$t("emails.status_T")},
            {value: 'P', text: this.$t("emails.status_P")},
            {value: 'U', text: this.$t("emails.status_U")},
            {value: 'C', text: this.$t("emails.status_C")},
          ]
        }
      },
      /* User profile */
      isUserPaneDisplayed: false,
      userTabs:[],
      userTabsActive: '',
      userMedia: {
        media: [],
        index: -1
      },
    }
  }
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" />
    <div class="position-relative">
      <b-button
          class="position-fixed"
          style="top: 77px; right: 15px;z-index:150"
          v-show="!isUserPaneDisplayed && userTabs.length > 0"
          @click="openUserPane"
          variant="outline-primary">
        <i class="bx bxs-cog"></i> Show Panel
      </b-button>

      <!-- Filters -->
      <div class="card">
        <div class="card-body">
          <!-- Search -->
          <b-form inline class="row gy-2 gx-3 align-items-center" id="emailsSearchForm" @submit.stop.prevent>
            <div class="col-sm-auto">
              <b-form-select
                  id="emailsSearchTypeFilter"
                  v-model="filters.searchType.selected"
                  class="form-select me-lg-1"
                  :options="filters.searchType.options">
              </b-form-select>
            </div>
            <div class="col-sm-auto">
              <b-form-input
                  id="emailsSearchData"
                  class="me-lg-1"
                  v-model="filters.searchData"
                  placeholder="Search…">
              </b-form-input>
            </div>
            <div class="col-sm-auto">
              <b-button variant="primary" class="w-md" @click="processSearch(true)">{{ $t("searchBtn") }}</b-button>
            </div>
          </b-form>
          <hr />
          <!-- filters -->
          <b-form inline class="row gy-2 gx-3 align-items-center" id="emailsFiltersForm" @submit.stop.prevent>

            <!-- dates -->
            <div class="col-sm-auto d-flex">
              <b-form-select
                  id="emailsDatesFilter"
                  @change="handleDateChange"
                  v-model="filters.dates.selected"
                  class="form-select me-lg-1"
                  :options="filters.dates.options">
              </b-form-select>
              <date-picker
                  v-show="filters.dates.selected === 'custom'"
                  :lang="page.localePicker"
                  :placeholder="this.$t('choose_date_interval')"
                  type="date"
                  value-type="format"
                  show-week-number
                  v-model="filters.dates.dateRange"
                  class="me-lg-2"
                  range append-to-body ></date-picker>

            </div>

            <!-- Countries -->
            <div class="col-sm-auto">
              <b-form-select
                  id="emailsCountriesFilter"
                  v-model="filters.countries.selected"
                  class="form-select me-lg-1"
                  :options="filters.countries.options">
              </b-form-select>
            </div>

            <!-- Genders -->
            <div class="col-sm-auto">
              <b-form-select
                  id="emailsGenderFilter"
                  v-model="filters.genders.selected"
                  class="form-select me-lg-1"
              >
                <option v-for="ct in filters.genders.options" :key="ct.value" :value="ct.value">{{ ct.text }}</option>
              </b-form-select>
            </div>

            <!-- Domains -->
            <div class="col-sm-auto">
              <b-form-select
                  id="emailsDomainFilter"
                  v-model="filters.domains.selected"
                  class="form-select me-lg-1"
              >
                <option v-for="ct in filters.domains.options" :key="ct.value" :value="ct.value">{{ ct.text }}</option>
              </b-form-select>
            </div>

            <!-- Types -->
            <div class="col-sm-auto">
              <b-form-select
                  id="emailsTypeFilter"
                  v-model="filters.types.selected"
                  class="form-select me-lg-1"
              >
                <option v-for="ct in filters.types.options" :key="ct.value" :value="ct.value">{{ ct.text }}</option>
              </b-form-select>
            </div>

            <!-- Status -->
            <div class="col-sm-auto">
              <b-form-select
                  id="emailsStatusFilter"
                  v-model="filters.status.selected"
                  class="form-select me-lg-1"
              >
                <option v-for="st in filters.status.options" :key="st.value" :value="st.value">{{ st.text }}</option>
              </b-form-select>
            </div>

            <!-- Organic -->
            <div class="col-sm-auto">
              <b-form-select
                  id="emailsOrganicFilter"
                  v-model="filters.organic.selected"
                  class="form-select me-lg-1"
                  :options="filters.organic.options">
              </b-form-select>
            </div>

            <!-- Ads -->
            <div class="col-sm-auto">
              <b-form-select
                  id="emailsAdsFilter"
                  v-model="filters.ads.selected"
                  class="form-select me-lg-1"
                  :options="filters.ads.options">
              </b-form-select>
            </div>

            <div class="col-sm-auto">
              <b-button variant="primary" class="w-md" @click="processFilters">{{ $t("find") }}</b-button>
            </div>
          </b-form>
        </div>
      </div>

      <!-- Loader -->
      <div class="row d-flex justify-content-center">
        <vue-ellipse-progress
            v-show="isLoading"
            :loading="isLoading"
            :angle="-90"
            color="#50a5f1"
            emptyColor="#999"
            emptyColorFill="#f8f8fb"
            :size="100"
            :thickness="3"
            :emptyThickness="2"
            lineMode="out 2"
            animation="bounce 700 400"
            fontColor="#666"
            :half="false"
            class="mt-5 mb-5"
            fontSize="1rem">
        </vue-ellipse-progress>
      </div>

      <!-- Stats Plain -->
      <div class="row" v-if="emailsSearch === 'filters'">
        <!-- Delivered -->
        <div class="col-lg-2">
          <div class="card mini-stats-wid">
            <div class="card-body">
              <div class="media">
                <div class="media-body">
                  <p class="text-dark fw-medium mb-2">{{ $t("emails.delivered") }}</p>
                  <h4 class="mb-0">{{ stats.delivered }}</h4>
                </div>

                <div class="avatar-sm align-self-center mini-stat-icon rounded-circle ">
                  <span class="avatar-title bg-light text-dark">
                    <i class="bx bx-archive-in font-size-24"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Opened -->
        <div class="col-lg-2">
          <div class="card mini-stats-wid">
            <div class="card-body">
              <div class="media">
                <div class="media-body">
                  <p class="text-dark fw-medium mb-2">{{ $t("emails.opened") }}</p>
                  <h4 class="mb-0">{{ stats.opened }}</h4>
                </div>

                <div class="avatar-sm align-self-center mini-stat-icon rounded-circle ">
                  <span class="avatar-title bg-success">
                    <i class="bx bxs-file-plus font-size-24"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Clicked -->
        <div class="col-lg-2">
          <div class="card mini-stats-wid">
            <div class="card-body">
              <div class="media">
                <div class="media-body">
                  <p class="text-dark fw-medium mb-2">{{ $t("emails.clicked") }}</p>
                  <h4 class="mb-0">{{ stats.clicked }}</h4>
                </div>

                <div class="avatar-sm align-self-center mini-stat-icon rounded-circle ">
                  <span class="avatar-title bg-info">
                    <i class="bx bxs-file font-size-24"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Error -->
        <div class="col-lg-2">
          <div class="card mini-stats-wid">
            <div class="card-body">
              <div class="media">
                <div class="media-body">
                  <p class="text-muted fw-medium mb-2">{{ $t("emails.error") }}</p>
                  <h4 class="mb-0">{{ stats.error }}</h4>
                </div>

                <div class="avatar-sm align-self-center mini-stat-icon rounded-circle ">
                  <span class="avatar-title bg-danger">
                    <i class="bx bx-error font-size-24"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Valided -->
        <div class="col-lg-2">
          <div class="card mini-stats-wid">
            <div class="card-body">
              <div class="media">
                <div class="media-body">
                  <p class="text-muted fw-medium mb-2">{{ $t("emails.valided") }}</p>
                  <h4 class="mb-0">{{ stats.valided }} <small>({{ stats.validedPerc}}%)</small></h4>
                </div>

                <div class="avatar-sm align-self-center mini-stat-icon rounded-circle ">
                  <span class="avatar-title bg-pink">
                    <i class="bx bx-stop-circle font-size-24"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Certified -->
        <div class="col-lg-2">
          <div class="card mini-stats-wid">
            <div class="card-body">
              <div class="media">
                <div class="media-body">
                  <p class="text-dark fw-medium mb-2">{{ $t("emails.certified") }}</p>
                  <h4 class="mb-0">{{ stats.certified }} <small>({{ stats.certifiedPerc}}%)</small></h4>
                </div>

                <div class="avatar-sm align-self-center mini-stat-icon rounded-circle">
                  <span class="avatar-title bg-dark">
                    <i class="bx bx-minus-circle font-size-24"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row" v-if="emailsSearch === 'filters'">

        <!-- graph Domain -->
        <div class="col-12 col-lg-3">
          <div class="card">
            <div class="card-body" id="cardChartDomains">
              <h5 class="card-title">Domains</h5>
              <apexchart
                  ref="emailsDomainsChart"
                  height="300"
                  type="donut"
                  dir="ltr"
                  :series="domainsGraph.series"
                  :options="domainsGraph.chartOptions"
              ></apexchart>
            </div>
          </div>
        </div>

        <!-- graph Types -->
        <div class="col-12 col-lg-3">
          <div class="card">
            <div class="card-body" id="cardChartTypes">
              <h5 class="card-title">Types</h5>
              <apexchart
                  ref="emailsTypesChart"
                  height="300"
                  type="donut"
                  dir="ltr"
                  :series="typesGraph.series"
                  :options="typesGraph.chartOptions"
              ></apexchart>
            </div>
          </div>
        </div>

        <!-- History -->
        <div class="col-12 col-lg-6">
          <div class="card">
            <div class="card-body" id="cardChartEmailsHistory">
              <h5 class="card-title">History</h5>
              <apexchart
                  ref="emailsHistoryChart"
                  class="apex-charts"
                  type="bar"
                  dir="ltr"
                  height="300"
                  :series="historyGraph.series"
                  :options="historyGraph.chartOptions"
              ></apexchart>
            </div>
          </div>
        </div>

      </div>

      <!-- Top list tools -->
      <div class="row mb-3">
        <!-- Display settings -->
        <div class="col-lg-auto col-12">
          <b-select class="form-select d-inline w-auto m-1" v-model="pageSizes.selected" @change="handleDisplayChange">
            <option v-for="size in pageSizes.options" :key="size" :value="size">{{ size }} {{ $t('pageSize') }}</option>
          </b-select>

          {{ count }} {{ $t('resultsCount') }}
        </div>
        <!-- Pagination -->
        <div class="col-lg-auto col-12">
          <div class="m-1 pagination-rounded">
            <b-pagination
                v-model="page"
                :total-rows="count"
                :per-page="pageSizes.selected"
                first-number
                last-number
                @change="handlePageChange"
                aria-controls="emails-list"
            ></b-pagination>
          </div>
        </div>
      </div>

      <!-- Result list -->
      <div class="row bg-white" v-if="!isLoading && emails.length > 0">
        <div class="table-responsive">
          <table
              id="emails-list"
              class="table project-list-table table-nowrap table-centered table-borderless align-middle"
          >
            <thead>
            <tr>
              <th scope="col">{{ $t('emails.createdAt') }}</th>
              <th scope="col">{{ $t('emails.status') }}</th>
              <th scope="col">{{ $t('emails.type') }}</th>
              <th scope="col">{{ $t('emails.domain') }}</th>
              <th scope="col">{{ $t('emails.email') }}</th>
              <th scope="col">{{ $t('emails.subject') }}</th>
              <th scope="col">{{ $t('emails.user') }}</th>
              <th scope="col">{{ $t('emails.actionAt') }}</th>
              <th scope="col">{{ $t('action') }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="email in emails" :key="email.emailId" @contextmenu.prevent.stop="showContextMenu($event, email)">
              <td>
                <p class="text-muted font-size-12">{{ formatDateString(email.createdAt, 'DD MMM YYYY HH:mm') }}</p>
              </td>
              <td>
                <span
                    class="badge"
                    :class="{
                      'bg-success text-light': `${email.status}` === 'O',
                      'bg-info text-light': `${email.status}` === 'A',
                      'bg-danger text-light': `${email.status}` === 'F' || `${email.status}` === 'E' || `${email.status}` === 'T' || `${email.status}` === 'P',
                      'bg-light text-dark': `${email.status}` === 'D',
                      'bg-warning text-dark': `${email.status}` === 'U' || `${email.status}` === 'C',
                    }"
                >{{ $t(`emails.status_${email.status}`) }}</span>
              </td>
              <td>
                <p class="text-muted font-size-12"><strong>{{ email.typeEmail }}</strong></p>
              </td>
              <td>
                <p class="font-size-12" >
                  <span
                      class="text-muted d-inline-block me-2"
                      :class="{
                        'text-info': `${email.sendingDomain}` === 'spreedl.app',
                        'text-warning': `${email.sendingDomain}` === 'spreedl.info',
                      }">{{ email.sendingDomain }} </span>
                </p>
              </td>
              <td>
                <p class="text-muted font-size-12"><b>{{ email.email }}</b>
                  <span v-if="!_isEmpty(email.mailgunMessageId)"><br><small>{{ email.mailgunMessageId }}</small></span>
                </p>
              </td>
              <td>
                <p class="text-muted font-size-12">{{ email.subject }}</p>
              </td>
              <td>
                <div class="media">
                  <div class="avatar-xs">
                    <img
                        :id="`email-user-${email.emailId}`"
                        :src="require('@/assets/images/users/default-user.png')"
                        class="d-flex rounded-circle avatar-xs" />
                  </div>
                  <div class="media-body ms-1">
                    <h5 class="font-size-14 mb-0">
                      <span
                          :class="{
                          'text-primary': email.sex === 1,
                          'text-danger': email.sex === 2,
                          'text-dark': email.sex === 0,
                        }"
                      >
                        {{ email.name || '-' }}
                      </span> {{ getAge(email.birth) }}</h5>
                    <small class="text-muted">#{{ email.userId }}</small>
                  </div>
                </div>
              </td>
              <td>
                <p class="text-muted font-size-12">{{ formatDateString(email.actionAt, 'lll') }}</p>
              </td>
              <td>
                <b-dropdown
                    class="card-drop"
                    variant="white"
                    menu-class="dropdown-menu-end"
                    right
                    toggle-class="p-0"
                >
                  <template v-slot:button-content>
                    <i class="mdi mdi-dots-horizontal font-size-18"></i>
                  </template>
                  <b-dropdown-item
                      href="javascript: void(0);"
                      @click="showUser(email.userId)"
                  >{{ $t("show_user") }}</b-dropdown-item>
                  <b-dropdown-item
                      href="javascript: void(0);"
                      @click="searchUser(email.userId)"
                  >{{ $t("search_user") }}</b-dropdown-item>
                </b-dropdown>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Result Empty -->
      <div class="row" v-if="!isLoading && emails.length === 0">
        <div class="col-12">
          <div class="card">
            <div class="card-body">
              <h4 class="card-title">Empty Result</h4>
              <p>No Emails were found for these filters or search. Try again.</p>
            </div>
          </div>
        </div>
      </div>

      <!-- bottom list tools -->
      <div class="row mb-3">
        <!-- Display settings -->
        <div class="col-lg-auto col-12">
          <b-select class="form-select d-inline w-auto m-1" v-model="pageSizes.selected" @change="handleDisplayChange">
            <option v-for="size in pageSizes.options" :key="size" :value="size">{{ size }} {{ $t('pageSize') }}</option>
          </b-select>
        </div>
        <!-- Pagination -->
        <div class="col-lg-auto col-12">
          <div class="m-1 pagination-rounded">
            <b-pagination
                v-model="page"
                :total-rows="count"
                :per-page="pageSizes.selected"
                first-number
                last-number
                @change="handlePageChange"
                aria-controls="emails-list"
            ></b-pagination>
          </div>
        </div>
      </div>

    </div>
    <b-sidebar id="sidebar-right" right v-model="isUserPaneDisplayed">
      <vue-tabs
          v-model="userTabsActive"
          active-tab-color="#213213"
          active-text-color="white">
        <v-tab v-for="userTab in userTabs" :key="userTab.userId" :title="userTab.userId" >
          <div slot="title">{{ userTab.name }} <i @click.stop="removeUserTab(userTab.userId)" class="bx bx-x-circle" ></i></div>
          <UserProfile
              :userId="null"
              :user="userTab"
              v-on:SHOW_MEDIA="showUserMedia">
          </UserProfile>
        </v-tab>
      </vue-tabs>
    </b-sidebar>

    <vue-simple-context-menu
        element-id="emailsContextMenu"
        :options="optionsContext"
        ref="emailsContextMenu"
        @option-clicked="processContextMenu"
    />

    <CoolLightBox
        :items="userMedia.media"
        :index="userMedia.index"
        :effect="'fade'"
        @close="userMedia.index = null">
    </CoolLightBox>

  </Layout>
</template>

<style>
.b-sidebar-right {
  width:35%;
}

.b-sidebar-body {
  padding:16px;
}
</style>
